import { useQuery } from "@tanstack/react-query";
import { cctvService, type Cctv } from "@safers/api";
import { cctvMedia } from "@/services";

export interface CCTVStreamItem {
  streamName: string;
  displayName: string;
  siteId: number;
  siteName: string;
  whepUrl: string;
  whepUrlLow: string | null;
  cctv: Cctv;
}

function siteKey(siteId?: number): number | string {
  if (siteId === undefined) return "all";
  return Number.isFinite(siteId) ? siteId : "invalid";
}

export const cctvKeys = {
  all: ["cctv"] as const,
  streams: (siteId?: number) => ["cctv", "streams", siteKey(siteId)] as const,
};

type StreamableCctv = Cctv & { streamName: string; site: NonNullable<Cctv["site"]> };

const isStreamable = (c: Cctv): c is StreamableCctv => Boolean(c.streamName && c.site);

function toStreamItem(c: StreamableCctv): CCTVStreamItem {
  return {
    streamName: c.streamName,
    displayName: c.name,
    siteId: c.site.id,
    siteName: c.site.name,
    whepUrl: cctvMedia.getWHEPUrl(c.streamName),
    whepUrlLow: c.streamNameLow ? cctvMedia.getWHEPUrl(c.streamNameLow) : null,
    cctv: c,
  };
}

const selectStreamItems = (cctvs: Cctv[]) => cctvs.filter(isStreamable).map(toStreamItem);

export function useCCTVStreams(siteId?: number) {
  const query = useQuery({
    queryKey: cctvKeys.streams(siteId),
    queryFn: () => cctvService.list(siteId),
    select: selectStreamItems,
  });

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
